import {
  BarChart3,
  Building2,
  Car,
  CarFront,
  ClipboardCheck,
  Columns3,
  FileSignature,
  FileText,
  FolderCog,
  GanttChart,
  LineChart,
  List,
  MapPin,
  Receipt,
  Settings,
  ShieldAlert,
  Users,
  Workflow,
  Wrench,
} from 'lucide-react';
import { moduleAreaForPath } from '@hetivo/hetivo-lib-types';
import type { ModuleAreaKey } from '@hetivo/hetivo-lib-types';
import type { NavigationItem } from '@hetivo/hetivo-lib-web-utils';
import { INSPECTIONS_LIST_PERMISSION, INSPECTIONS_PATH } from '&/features/inspections/paths';
import { INSPECTION_SETTINGS_PERMISSION, INSPECTION_SETTINGS_PATH } from '&/features/settings/inspections/paths';
import { CONTRACTS_LIST_PERMISSION, LEASING_ACCESS_PERMISSION } from '&/features/leasing/paths';
import { SERVICE_REMINDERS_VIEW_PERMISSION } from '&/features/service-reminders/paths';
import { isTerritoryScopedAdmin } from '&/utils/territory-admin';
import type { MasterContextValue } from '&/master';
import type { UseUserScopesReturn } from '&/hooks/use-user-scopes';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BuildSettingsNavItemsParams = Pick<
  UseUserScopesReturn,
  | 'canAccessSites'
  | 'canAccessTerritories'
  | 'canAccessTenantSettings'
  | 'hasMultipleSites'
  | 'hasMultipleTerritories'
  | 'preferSitesTab'
  | 'singleSiteId'
  | 'singleTerritoryId'
>;

export type BuildMasterContextSettingsNavItemsParams = Pick<MasterContextValue, 'siteId' | 'territoryId'> &
  BuildSettingsNavItemsParams;

export type BuildSidebarNavItemsParams = BuildMasterContextSettingsNavItemsParams &
  Pick<UseUserScopesReturn, 'isTenantScoped'> & {
    /** Entitlement predicate — hides entries the module registry assigns to an area the tenant lacks. */
    hasModule: (areaKey: ModuleAreaKey) => boolean;
    /**
     * True once `/users/me` and the permissions have loaded. Until then the Territory Admin group is not
     * built (SWOOP-302): `isTenantScoped` reads false while loading, so a tenant-wide Fleet Manager would
     * briefly get it. The flat Settings link shows meanwhile.
     */
    isScopeResolved: boolean;
  };

// ─── Top-level nav items (a mix of real routes and "SOON" placeholders) ──────

/**
 * Top-level sidebar items — some are real routes, some are inert "SOON" placeholders for
 * modules that have not shipped. The list is deliberately mixed: order in the sidebar is
 * product-driven, so a shipped module keeps its slot among the ones still to come.
 *
 * - Dashboard is unlocked and points to the root route `/`.
 * - Vehicles is a collapsible category (like Settings) with a single child,
 *   "List of Vehicles" → `/vehicles`, gated by `vehicles:list`. There is no
 *   "Add Vehicle" child — the Add-Vehicle wizard is reached only via the
 *   "Add Vehicle" button on the vehicles list page (SWOOP-130).
 * - Reminders (SWOOP-281) is a live route gated on `service-plans:list`, with a dynamic Overdue count badge
 *   (set in `_authenticated.tsx`, hidden at 0). It is a Maintenance surface in the module registry, so it is
 *   hidden from tenants without that module (SWOOP-277).
 * - Issues (SWOOP-255) is a live route gated on `issues:list`; it is a Maintenance surface, so it is
 *   also hidden from tenants without the module. It carries a dynamic Open-count badge, set in
 *   `_authenticated.tsx`.
 * - Inspections (SWOOP-378) is a live route gated on `inspections:list` (the Inspector role). It is a surface
 *   of the Inspections & Compliance module in the registry (its `/inspections` path), so it is hidden from
 *   tenants without the module. The page is empty for now; the screens land with SWOOP-373..377.
 * - Reports is a placeholder that becomes a real route in a future sprint. It stays intentionally inert
 *   (`disabled: true`) with a SOON badge, carries no route, has no `requiredPermission`, and must not
 *   trigger navigation on click. It is not a module surface, so it stays visible to every tenant.
 * - Leasing (SWOOP-298) is a collapsible category placed right after Vehicles. Its group path
 *   `/leasing` is a Leasing surface in the module registry, so the whole group is hidden from tenants
 *   without the module, and it is gated on `leasing:access` for roles without it. Contracts is a link;
 *   Order pipeline, Leased vehicles, Billing and Analytics are inert SOON items, like Inspections and
 *   Reports. Their routes still exist and show the Coming soon state when opened by address, so they
 *   are deep-linkable until their epics ship.
 * - Document test (SWOOP-267) is a real, navigable route — a temporary tenant-admin-only
 *   test screen for the SWOOP-265 epic, marked with a TEST badge and gated on
 *   `document-templates:list`. It is removed when the epic lands.
 */
const TOP_LEVEL_NAV_ITEMS: NavigationItem[] = [
  { icon: BarChart3, labelKey: 'navigation.dashboard', path: '/' },
  {
    children: [
      {
        icon: List,
        labelKey: 'navigation.vehicles.list',
        path: '/vehicles',
        requiredPermission: 'vehicles:list',
      },
    ],
    icon: Car,
    labelKey: 'navigation.vehicles',
    path: '/vehicles',
    requiredPermission: 'vehicles:list',
  },
  {
    children: [
      {
        // The Contracts link needs the contract read permission, not just leasing:access — a Location
        // Manager has leasing:access but no contracts:list, so the Leasing group shows (its SOON items)
        // without a Contracts link (SWOOP-345/SWOOP-344).
        icon: FileSignature,
        labelKey: 'navigation.leasing.contracts',
        path: '/leasing/contracts',
        requiredPermission: CONTRACTS_LIST_PERMISSION,
      },
      {
        badgeLabelKey: 'navigation.soon',
        disabled: true,
        icon: Workflow,
        labelKey: 'navigation.leasing.orderPipeline',
        path: '/leasing/order-pipeline',
      },
      {
        badgeLabelKey: 'navigation.soon',
        disabled: true,
        icon: CarFront,
        labelKey: 'navigation.leasing.leasedVehicles',
        path: '/leasing/leased-vehicles',
      },
      {
        badgeLabelKey: 'navigation.soon',
        disabled: true,
        icon: Receipt,
        labelKey: 'navigation.leasing.billing',
        path: '/leasing/billing',
      },
      {
        badgeLabelKey: 'navigation.soon',
        disabled: true,
        icon: LineChart,
        labelKey: 'navigation.leasing.analytics',
        path: '/leasing/analytics',
      },
    ],
    icon: FileText,
    labelKey: 'navigation.leasing',
    path: '/leasing',
    requiredPermission: LEASING_ACCESS_PERMISSION,
  },
  {
    icon: GanttChart,
    labelKey: 'navigation.reminders',
    path: '/reminders',
    requiredPermission: SERVICE_REMINDERS_VIEW_PERMISSION,
  },
  {
    icon: ClipboardCheck,
    labelKey: 'navigation.inspections',
    path: INSPECTIONS_PATH,
    requiredPermission: INSPECTIONS_LIST_PERMISSION,
  },
  {
    icon: ShieldAlert,
    labelKey: 'navigation.issues',
    path: '/issues',
    requiredPermission: 'issues:list',
  },
  // Work orders (SWOOP-325): a real route, gated on `work-orders:list` and — like Reminders and Issues —
  // hidden from a tenant without the Maintenance module (its `/work-orders` path is a Maintenance surface
  // in the registry, so `isEntitled` filters it out).
  {
    icon: Wrench,
    labelKey: 'navigation.workOrders',
    path: '/work-orders',
    requiredPermission: 'work-orders:list',
  },
  {
    badgeLabelKey: 'navigation.soon',
    disabled: true,
    icon: Columns3,
    labelKey: 'navigation.reports',
    path: '/reports',
  },
  // SWOOP-267: temporary tenant-admin-only test screen for the SWOOP-265 epic. It is NOT a
  // product feature — it carries a TEST badge and is removed once the epic lands. Gated on
  // `document-templates:list`, granted to TENANT_ADMIN alone (see the route's own comment for
  // why not `documents:*`), so no other role sees the entry; the route repeats the check for
  // direct URLs.
  {
    badgeLabelKey: 'navigation.test',
    icon: FileText,
    labelKey: 'navigation.documentTest',
    path: '/document-test',
    requiredPermission: 'document-templates:list',
  },
];

// ─── Static Settings children that don't depend on scope ─────────────────────

const SETTINGS_STATIC_CHILDREN: NavigationItem[] = [
  {
    icon: MapPin,
    labelKey: 'navigation.settings.territories',
    path: '/settings/territories',
    requiredPermission: 'tenants:read',
  },
  {
    icon: Users,
    labelKey: 'navigation.settings.users',
    path: '/settings/users',
    requiredPermission: 'users:list',
  },
  {
    icon: FolderCog,
    labelKey: 'navigation.settings.fleet',
    path: '/settings/fleet',
    requiredPermission: 'tenants:read',
  },
  {
    icon: Wrench,
    labelKey: 'navigation.settings.maintenance',
    path: '/settings/maintenance',
    requiredPermission: 'service-operations:list',
  },
  // SWOOP-378: Settings > Inspections, gated on `inspection-templates:manage` — the permission the templates API
  // itself requires, which the Tenant Admin holds and the Inspector and Account Manager do not. Its `/settings/inspections` path is an Inspections-module surface in the
  // registry, so `isEntitled` hides it from a tenant without the module, like Settings > Maintenance above.
  {
    icon: ClipboardCheck,
    labelKey: 'navigation.settings.inspections',
    path: INSPECTION_SETTINGS_PATH,
    requiredPermission: INSPECTION_SETTINGS_PERMISSION,
  },
];

// ─── Territory Admin Settings (SWOOP-302) ──────────────────────────────────

/** The Territory Admin's Settings: their territories and users, without Tenant Profile. */
const TERRITORY_ADMIN_SETTINGS_GROUP: NavigationItem = {
  children: [
    {
      icon: MapPin,
      labelKey: 'navigation.settings.territories',
      path: '/settings/territories',
      requiredPermission: 'territories:list',
    },
    {
      icon: Users,
      labelKey: 'navigation.settings.users',
      path: '/settings/users',
      requiredPermission: 'users:list',
    },
  ],
  icon: Settings,
  labelKey: 'navigation.settings',
  path: '/settings',
  requiredPermission: 'territories:list',
};

// ─── Module-owned entries ───────────────────────────────────────────────────

/**
 * True unless the entry belongs to a module area the tenant is not entitled to.
 *
 * Ownership comes from the module registry (`moduleAreaForPath`), not from a list kept here. The
 * registry's surfaces are also what the Modules tab advertises, so an entry cannot be hidden from a
 * tenant without the tab listing it, or listed without being hidden (SWOOP-277). To make a new entry
 * module-owned, give its surface a `path` in `packages/hetivo-lib-types/src/modules.ts`.
 */
function isEntitled(item: NavigationItem, hasModule: BuildSidebarNavItemsParams['hasModule']): boolean {
  const area = moduleAreaForPath(item.path);
  return area === undefined || hasModule(area);
}

// ─── Dynamic builders ─────────────────────────────────────────────────────────

/**
 * Builds the full sidebar nav items.
 *
 * - For `tenants:read` users: the collapsible Settings group with L2 children
 *   (Tenant Profile, Territories (navigable), Users, Fleet).
 *   Tenant Profile path is derived via `buildMasterContextSettingsNavItems` to
 *   preserve the existing scope/permission cascade exactly.
 *
 * - For `territories:list` / `locations:list` users (no `tenants:read`): a flat Settings
 *   leaf item pointing to their territory/site settings (preserves the pre-existing
 *   sidebar behaviour for territory/site scoped users).
 *
 * - No settings access at all: only the top-level placeholder items.
 */
export function buildSidebarNavItems(params: BuildSidebarNavItemsParams): NavigationItem[] {
  const { canAccessTenantSettings, hasModule } = params;
  const settingsItems = buildMasterContextSettingsNavItems(params);
  const topLevelItems = TOP_LEVEL_NAV_ITEMS.filter((item) => isEntitled(item, hasModule));

  // No settings access at all → return only placeholder top-level items
  if (settingsItems.length === 0) return topLevelItems;

  // Territory Admin (SWOOP-302) → a Settings group with their territories and users. Both pages are
  // scoped to the territories they administer by the backend; Tenant Profile stays with the Tenant Admin.
  if (isTerritoryScopedAdmin(params)) {
    return [...topLevelItems, TERRITORY_ADMIN_SETTINGS_GROUP];
  }

  // Other territory/site scoped users (no tenants:read) → keep the original flat Settings link
  if (!canAccessTenantSettings) {
    const flatSettingsItem: NavigationItem = {
      icon: Settings,
      labelKey: 'navigation.settings',
      path: settingsItems[0].path,
    };
    return [...topLevelItems, flatSettingsItem];
  }

  // Tenant admin → collapsible Settings group with L2 children.
  // Tenant Profile always points to the flat /settings/tenant-profile route.
  const tenantProfileChild: NavigationItem = {
    icon: Building2,
    labelKey: 'navigation.settings.tenantProfile',
    path: '/settings/tenant-profile',
    requiredPermission: 'tenants:read',
  };

  // Same registry lookup as the top-level items above, so Settings › Maintenance and the Maintenance menu area
  // share one check (SWOOP-276 "absent, not broken"; SWOOP-277 "cannot diverge").
  const settingsChildren = SETTINGS_STATIC_CHILDREN.filter((child) => isEntitled(child, hasModule));

  const settingsGroup: NavigationItem = {
    children: [tenantProfileChild, ...settingsChildren],
    icon: Settings,
    labelKey: 'navigation.settings',
    path: '/settings',
    requiredPermission: 'tenants:read',
  };

  return [...topLevelItems, settingsGroup];
}

/**
 * Builds the bottom sidebar navigation items using MasterContext as the
 * primary source of truth for the Settings destination URL.
 *
 * Priority:
 * 1. MasterContext has siteId      → `/settings/locations/$siteId`
 * 2. MasterContext has territoryId → `/settings/territories/$territoryId`
 * 3. MasterContext at tenant level (both null) → delegate to `buildSettingsNavItems`
 *
 * The Settings entry is only shown when the user has at least one of
 * `tenants:read`, `territories:list`, or `locations:list`.
 *
 * @deprecated Use `buildSidebarNavItems` for the frontoffice sidebar — this
 * function is preserved for the Tenant Profile destination resolution used
 * internally by `buildSidebarNavItems`.
 */
export function buildMasterContextSettingsNavItems(params: BuildMasterContextSettingsNavItemsParams): NavigationItem[] {
  const {
    canAccessSites,
    canAccessTerritories,
    canAccessTenantSettings,
    preferSitesTab,
    siteId,
    territoryId,
    ...staticParams
  } = params;
  const { hasMultipleTerritories } = staticParams;

  const hasAnySettingsPermission = canAccessTenantSettings || canAccessTerritories || canAccessSites;
  if (!hasAnySettingsPermission) return [];

  // MasterContext has a site selected → link directly to that location's settings
  if (siteId) {
    return [{ icon: Settings, labelKey: 'navigation.settings', path: `/settings/locations/${siteId}` }];
  }

  // MasterContext has a territory selected → link directly to that territory's settings
  // Users with locations:list but no territories:list should land on the locations tab (the general tab requires territories:list)
  if (territoryId) {
    const subPath = preferSitesTab
      ? `/settings/territories/${territoryId}/locations`
      : `/settings/territories/${territoryId}`;
    return [{ icon: Settings, labelKey: 'navigation.settings', path: subPath }];
  }

  // Tenant level — fall back to static cascade
  const staticItems = buildSettingsNavItems({
    canAccessSites,
    canAccessTerritories,
    canAccessTenantSettings,
    preferSitesTab,
    ...staticParams,
  });
  if (staticItems.length > 0) return staticItems;

  // Cross-territory-sites fallback: the static cascade couldn't build a URL (no singleTerritoryId)
  // because this user's site scopes span multiple parent territories (hasMultipleTerritories=true from
  // resolvedScopes). Before MasterContext initializes, point them to /settings so the sidebar
  // item appears immediately. This only activates for users who span multiple territories but have
  // no territories:list permission and no singleTerritoryId to build a scoped URL from.
  if (canAccessSites && hasMultipleTerritories) {
    return [{ icon: Settings, labelKey: 'navigation.settings', path: '/settings' }];
  }

  return [];
}

/**
 * Builds the bottom sidebar navigation items dynamically based on the user's
 * permissions and scope flags (whether the user has access to multiple territories/sites).
 *
 * Returns **at most one** item — always labelled "Settings" with a `Settings` icon.
 * Only the destination URL varies based on the user's highest-privilege scope:
 *
 * 1. `tenants:read`                                                    → `/settings`
 * 2. `territories:list` + hasMultipleTerritories                       → `/settings/territories`
 * 3. `territories:list` + !hasMultipleTerritories + singleTerritoryId  → `/settings/territories/:id`
 * 4. `locations:list`  + hasMultipleSites                                  → `/settings/territories/:id/locations`
 * 5. `locations:list`  + !hasMultipleSites + singleSiteId                  → `/settings/locations/:id`
 * 6. (none of the above)                                               → empty array
 *
 * Each level is exclusive: once a match is found the cascade stops immediately.
 */
export function buildSettingsNavItems(params: BuildSettingsNavItemsParams): NavigationItem[] {
  const {
    canAccessSites,
    canAccessTerritories,
    canAccessTenantSettings,
    hasMultipleSites,
    hasMultipleTerritories,
    singleSiteId,
    singleTerritoryId,
  } = params;

  // 1. Tenant admin — highest priority
  if (canAccessTenantSettings) {
    return [{ icon: Settings, labelKey: 'navigation.settings', path: '/settings' }];
  }

  // 2 & 3. Territory admin — only when tenant settings are absent
  const territoryItem = resolveTerritoryItem({ canAccessTerritories, hasMultipleTerritories, singleTerritoryId });
  if (territoryItem) return [territoryItem];

  // 4 & 5. Site admin — only when no territories entry matched
  const siteItem = resolveSiteItem({ canAccessSites, hasMultipleSites, singleSiteId, singleTerritoryId });
  if (siteItem) return [siteItem];

  // 6. No matching permission/count combination
  return [];
}

// ─── Private resolvers ────────────────────────────────────────────────────────

interface ResolveTerritoryItemParams {
  canAccessTerritories: boolean;
  hasMultipleTerritories: boolean;
  singleTerritoryId: string | null;
}

function resolveTerritoryItem({
  canAccessTerritories,
  hasMultipleTerritories,
  singleTerritoryId,
}: ResolveTerritoryItemParams): NavigationItem | null {
  if (!canAccessTerritories) return null;

  if (hasMultipleTerritories) {
    return { icon: Settings, labelKey: 'navigation.settings', path: '/settings/territories' };
  }

  if (singleTerritoryId) {
    return { icon: Settings, labelKey: 'navigation.settings', path: `/settings/territories/${singleTerritoryId}` };
  }

  return null;
}

interface ResolveSiteItemParams {
  canAccessSites: boolean;
  hasMultipleSites: boolean;
  singleSiteId: string | null;
  singleTerritoryId: string | null;
}

function resolveSiteItem({
  canAccessSites,
  hasMultipleSites,
  singleSiteId,
  singleTerritoryId,
}: ResolveSiteItemParams): NavigationItem | null {
  if (!canAccessSites) return null;

  if (hasMultipleSites) {
    // Multi-location users need a territoryId to build the locations list URL.
    // singleTerritoryId is now always available for location-scoped users via useUserScopes.
    if (!singleTerritoryId) return null;
    return {
      icon: Settings,
      labelKey: 'navigation.settings',
      path: `/settings/territories/${singleTerritoryId}/locations`,
    };
  }

  if (singleSiteId) {
    return { icon: Settings, labelKey: 'navigation.settings', path: `/settings/locations/${singleSiteId}` };
  }

  return null;
}

// ─── Legacy static export (backward compat) ───────────────────────────────────

/**
 * Static sidebar items — Dashboard only.
 * Kept for backward compatibility; callers should prefer `buildSidebarNavItems`.
 */
export const SIDEBAR_NAV_ITEMS: NavigationItem[] = [{ icon: BarChart3, labelKey: 'navigation.dashboard', path: '/' }];
